import { InfoOutlined } from '@mui/icons-material'
import { Grid, IconButton, Paper, Tooltip } from '@mui/material'
import { Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { getI18n, useTranslation } from 'react-i18next'

import { getErrMsg, toastNeg, toastPos } from '../../common/toastHelper'
import { useStore } from '../../Provider'
import { ExitConfirmation } from '../OPcomponents/ExitConfirmation'
import { MuiButtonVariants, OPButton } from '../OPcomponents/OPButton'
import { OPMultiline } from '../OPcomponents/OPMultiline'
import { Toast } from '../OPcomponents/Toast'
import { overviewSchema } from '../OPcomponents/ValidationSchemas'
import '../styles/grid.scss'

import history from '../../history'
import { IEditedOverview } from '../../interfaces/persons/IPerson'
import { OPLoadingCircle } from '../OPcomponents/OPLoadingcircle'
import { OPSkillsButtons } from './OPSkillsButtons'
import AddNewLanguage from './overview/AddNewLanguage'
import LanguageTable from './overview/LanguageTable'

const Overview = (): JSX.Element => {
  const { t } = useTranslation()

  const rootStore = useStore()
  const { sidebarUser, fetchSidebarData } = rootStore.sidebarStore
  const { validatedUser, user } = rootStore.loginStore
  const {
    userObject,
    getUserOverview,
    showUserModal,
    setShowUserModal,
    editUserOverview,
    showLanguageModal,
    setShowLanguageModal,
    getCurrentLanguageToEdit,
    openLanguageEditModal,
    filteredLanguages,
    loadingOverview,
  } = rootStore.personStore

  useEffect(() => {
    // Always refetch if we don't have data or we navigated here after viewing another user's profile
    getUserOverview(validatedUser)
  }, [getUserOverview, validatedUser])

  useEffect(() => {
    return () => {
      setShowUserModal(false)
      setShowLanguageModal(false)
      getCurrentLanguageToEdit('')
    }
  }, [getCurrentLanguageToEdit, setShowLanguageModal, setShowUserModal])

  const onSubmit = async (values: IEditedOverview) => {
    try {
      await editUserOverview(validatedUser, values)
      toastPos(t('Toasts.EditSuccess'))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.EditError'))
    } finally {
      await getUserOverview(validatedUser)
      await fetchSidebarData(validatedUser, user.name)
      setShowUserModal(false)
    }
  }

  if (loadingOverview) {
    return <OPLoadingCircle />
  }

  return (
    <Grid>
      <Toast />
      <Grid container rowSpacing={4}>
        <Grid item xs={12}>
          <Grid container justifyContent={'space-between'}>
            <Grid item>
              <span className='profile--titlebigfont'>{t('Buttons.Overview')}</span>
            </Grid>
            <Grid item>
              <OPButton
                buttonVariant={MuiButtonVariants.primary_outlined}
                onClick={() => {
                  history.push('/profile/overview/cv/')
                }}
                label={t('Buttons.DownloadCV')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {!showUserModal ? (
            <Grid container spacing={4}>
              <Grid item>
                <span className='profile--titlecaption nopadding'>{`${t('Profile.ProfileTitle')}:`}</span>
                <span className='profile--deptext nopadding'>
                  {getI18n().language === 'en' && userObject?.titleEng !== ''
                    ? userObject.titleEng
                    : userObject?.titleFi !== ''
                    ? userObject.titleFi
                    : t('Profile.NoAddedTitle')}
                </span>
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <span className='profile--titlecaption nopadding'>{t('Profile.ProfileSummary')}:</span>
                </Grid>
                <Grid item xs={12}>
                  <span className='profile--deptext nopadding'>
                    {getI18n().language === 'en' && userObject?.summaryEng !== ''
                      ? userObject.summaryEng
                      : userObject?.summaryFi !== ''
                      ? userObject.summaryFi
                      : t('Profile.NoAddedSummary')}
                  </span>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <span className='profile--titlecaption nopadding'>{t('Profile.ProfileWorkExperienceDescription')}:</span>
                </Grid>
                <Grid item xs={12}>
                  <span className='profile--deptext nopadding'>
                    {getI18n().language === 'en' && userObject?.workExperienceDescriptionEng !== ''
                      ? userObject.workExperienceDescriptionEng
                      : userObject?.workExperienceDescriptionFi !== ''
                      ? userObject.workExperienceDescriptionFi
                      : t('Profile.NoAddedWorkExperience')}
                  </span>
                </Grid>
              </Grid>
              <Grid item xs={12} textAlign='right'>
                <OPButton
                  label={t('Buttons.Edit')}
                  buttonVariant={MuiButtonVariants.primary_outlined}
                  onClick={() => setShowUserModal(true)}
                />
              </Grid>
            </Grid>
          ) : (
            <Formik
              validationSchema={overviewSchema}
              initialValues={
                {
                  titleFi: sidebarUser.departmentFi,
                  titleEng: sidebarUser.departmentEng,
                  summaryFi: userObject.summaryFi,
                  summaryEng: userObject.summaryEng,
                  workExperienceDescriptionFi: userObject.workExperienceDescriptionFi,
                  workExperienceDescriptionEng: userObject.workExperienceDescriptionEng,
                } as IEditedOverview
              }
              onSubmit={onSubmit}
            >
              {({ handleChange, handleSubmit, values, isValid, dirty, isSubmitting, errors, touched }) => (
                <Paper className='profile--paper__addformpadding'>
                  <ExitConfirmation when={dirty} />
                  <Grid container spacing={4}>
                    <Grid item xs={7}>
                      <OPMultiline
                        name='titleFi'
                        value={values.titleFi}
                        label={t('Profile.ProfileTitle') + ' (FI) *'}
                        maxChars={100}
                        hasCounter
                        onChange={handleChange}
                        error={touched.titleFi && Boolean(errors.titleFi)}
                        errorText={touched.titleFi && errors.titleFi ? t(errors.titleFi) : ''}
                      />
                      <OPMultiline
                        name='titleEng'
                        value={values.titleEng}
                        label={t('Profile.ProfileTitle') + ' (EN) *'}
                        maxChars={100}
                        hasCounter
                        onChange={handleChange}
                        error={touched.titleEng && Boolean(errors.titleEng)}
                        errorText={touched.titleEng && errors.titleEng ? t(errors.titleEng) : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div style={{ position: 'relative' }}>
                        <OPMultiline
                          name='summaryFi'
                          value={values.summaryFi}
                          maxChars={800}
                          hasCounter
                          minRows={5}
                          label={
                            <span>
                              {t('Profile.ProfileSummary') + ' (FI) *'}
                              <Tooltip title={t('Profile.ProfileSummaryHelp')} arrow placement='right'>
                                <IconButton size='small' sx={{ padding: 0, ml: 0.5, verticalAlign: 'middle' }}>
                                  <InfoOutlined fontSize='small' color='primary' />
                                </IconButton>
                              </Tooltip>
                            </span>
                          }
                          onChange={handleChange}
                          error={touched.summaryFi && Boolean(errors.summaryFi)}
                          errorText={touched.summaryFi && errors.summaryFi ? t(errors.summaryFi) : ''}
                        />
                      </div>
                      <OPMultiline
                        name='summaryEng'
                        value={values.summaryEng}
                        maxChars={800}
                        hasCounter
                        minRows={5}
                        label={t('Profile.ProfileSummary') + ' (EN) *'}
                        onChange={handleChange}
                        error={touched.summaryEng && Boolean(errors.summaryEng)}
                        errorText={touched.summaryEng && errors.summaryEng ? t(errors.summaryEng) : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div style={{ position: 'relative' }}>
                        <OPMultiline
                          name='workExperienceDescriptionFi'
                          value={values.workExperienceDescriptionFi}
                          maxChars={800}
                          hasCounter
                          minRows={5}
                          label={
                            <span>
                              {t('Profile.ProfileWorkExperienceDescription') + ' (FI) *'}
                              <Tooltip title={t('Profile.ProfileWorkExperienceDescriptionHelp')} arrow placement='right'>
                                <IconButton size='small' sx={{ padding: 0, ml: 0.5, verticalAlign: 'middle' }}>
                                  <InfoOutlined fontSize='small' color='primary' />
                                </IconButton>
                              </Tooltip>
                            </span>
                          }
                          onChange={handleChange}
                          error={touched.workExperienceDescriptionFi && Boolean(errors.workExperienceDescriptionFi)}
                          errorText={
                            touched.workExperienceDescriptionFi && errors.workExperienceDescriptionFi
                              ? t(errors.workExperienceDescriptionFi)
                              : ''
                          }
                        />
                      </div>
                      <OPMultiline
                        name='workExperienceDescriptionEng'
                        value={values.workExperienceDescriptionEng}
                        maxChars={800}
                        hasCounter
                        minRows={5}
                        label={t('Profile.ProfileWorkExperienceDescription') + ' (EN) *'}
                        onChange={handleChange}
                        error={touched.workExperienceDescriptionEng && Boolean(errors.workExperienceDescriptionEng)}
                        errorText={
                          touched.workExperienceDescriptionEng && errors.workExperienceDescriptionEng
                            ? t(errors.workExperienceDescriptionEng)
                            : ''
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <OPSkillsButtons
                        noPadding
                        onSubmit={handleSubmit}
                        disabled={!(isValid && dirty) || isSubmitting}
                        bothDisabled={isSubmitting}
                        onClose={() => setShowUserModal(false)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Formik>
          )}
        </Grid>
        <hr className='profile--form__editdivider' />
        <Grid container justifyContent='space-between'>
          <Grid item>
            <span className='profile--subtitlefont'>{t('Language.LanguageSkills')}</span>
          </Grid>
          <Grid item className='griditem--addbutton'>
            <OPButton
              label={t('Buttons.AddNew')}
              buttonVariant={MuiButtonVariants.primary_outlined}
              onClick={() => {
                setShowLanguageModal()
                getCurrentLanguageToEdit('')
              }}
              disabled={showLanguageModal || showUserModal || openLanguageEditModal}
            />
          </Grid>
        </Grid>
        {showLanguageModal && (
          <Grid item xs={12}>
            <Paper className='profile--paper__addformpadding'>
              <Grid item xs={12} className='griditem--formitem__titlemargin'>
                <span className='profile--addnewfont nopadding'>{t('Language.AddNewLanguageSkill')}</span>
              </Grid>
              <AddNewLanguage />
            </Paper>
          </Grid>
        )}
        {filteredLanguages.length > 0 ? (
          <Grid item xs={12}>
            <LanguageTable />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper>
              <Grid item xs={12} className='griditem--formitem__titlemargin'>
                <span className='profile--notfound'>{t('Profile.NoAddedLanguages')}</span>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default observer(Overview)
